import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAgents } from '../../services/api';
import { runnableAgents, toTurns } from '../../services/runCenter';
import { readAgentRuleCount } from '../../services/protectBuild';
import useTestRun from '../../hooks/useTestRun';
import ProtectRun from './ProtectRun';
import { EYEBROW, MONO, PAGE_TITLE } from './typeScale';

/**
 * The Run Center: choose one of your agents, and use it with policy live.
 *
 * <h3>Why the list is shorter than your estate</h3>
 *
 * Only agents the platform says serve governed traffic appear — a native agent
 * once published, an ExteLet once execution ready. Offering a draft here would
 * promise a governed run the platform will refuse, and the verdict beside the
 * reply is the whole point of running it from inside Protect.
 *
 * <h3>Why the conversation is the platform's own</h3>
 *
 * The same `useTestRun` that drives the platform's Test Run panel drives this
 * screen; only the paint differs. Two implementations of "was that blocked?"
 * would eventually give two answers to the same question, and this product
 * cannot afford that disagreement.
 */
export default function RunCenter({ scope, agentId, onRecord, onChange }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [pickedId, setPickedId] = useState(agentId || null);

  const orgId = scope?.orgId;
  const tenantId = scope?.tenantId;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        /* Scoped to the caller's org and tenant, like every other read on this
           surface — never a blanket list. */
        const res = await getAgents({ orgId, tenantId });
        const list = Array.isArray(res) ? res : (res?.data || []);
        if (alive) { setRows(Array.isArray(list) ? list : []); setError(null); }
      } catch (e) {
        /* A failed read is said out loud. Rendering it as an empty estate would
           tell somebody they have no agents, which is a different and false
           statement. */
        if (alive) { setRows([]); setError(e?.message || 'network'); }
      }
    })();
    return () => { alive = false; };
  }, [orgId, tenantId]);

  const runnable = useMemo(() => runnableAgents(rows), [rows]);

  /* Land on the agent we were handed, if it is one that can be run. */
  useEffect(() => {
    if (agentId && runnable.some((a) => a.id === agentId)) setPickedId(agentId);
  }, [agentId, runnable]);

  const picked = runnable.find((a) => a.id === pickedId) || null;

  /* What is holding the chosen agent, read from the platform for THAT agent.
     Re-read on every change of agent rather than carried over: a count from the
     last agent rendered beside this one is a wrong statement about protection,
     which is worse than none. `null` while unknown, so the chip says nothing
     rather than claiming zero (ProtectRun renders it only when it is a
     number). */
  const [ruleCount, setRuleCount] = useState(null);
  useEffect(() => {
    let alive = true;
    setRuleCount(null);
    if (!picked?.id) return undefined;
    (async () => {
      const { count } = await readAgentRuleCount(picked.id);
      if (alive) setRuleCount(count);
    })();
    return () => { alive = false; };
  }, [picked?.id]);
  const { messages, sending, send } = useTestRun(picked);
  const turns = useMemo(() => toTurns(messages), [messages]);

  const onSend = useCallback((text) => { send(text); }, [send]);

  return (
    <div style={{ minWidth: 0, width: '100%' }}>
      <h1 style={{ ...PAGE_TITLE, margin: '0 0 var(--p7-space-2)' }}>Run Center</h1>
      <p style={{ margin: '0 0 var(--p7-space-5)', maxWidth: '62ch',
                  font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                  color: 'var(--p7-ink-subtle)' }}>
        Use one of your protected agents, with every rule live. The verdict on each answer sits
        under it, and the whole exchange is recorded.
      </p>

      {error ? (
        <div className="p7-note p7-note--deny" style={{ marginBottom: 'var(--p7-space-4)' }}>
          Could not read your agents just now. Reopen this page to try again.
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--p7-space-5)', alignItems: 'flex-start',
                    minWidth: 0 }}>
        <aside style={{ width: '248px', flex: 'none', minWidth: 0 }}>
          <h2 style={{ margin: '0 0 var(--p7-space-2)', ...EYEBROW }}>Your agents</h2>

          {rows === null ? (
            <p style={PICKER_NOTE}>Reading your agents…</p>
          ) : runnable.length === 0 ? (
            /* Not an empty box: the reason, and what changes it. */
            <p style={PICKER_NOTE}>
              None of your agents is published or execution ready yet, so there is nothing here
              to run. Clear an agent’s gates and it will appear.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex',
                         flexDirection: 'column', gap: '4px' }}>
              {runnable.map((a) => {
                const here = a.id === pickedId;
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      aria-current={here ? 'true' : undefined}
                      onClick={() => setPickedId(a.id)}
                      style={{
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                        padding: '9px 11px', borderRadius: 'var(--p7-radius-control)',
                        border: `1px solid ${here ? 'var(--p7-accent)' : 'var(--p7-line)'}`,
                        background: here ? 'var(--p7-accent-bg)' : 'var(--p7-surface)',
                        font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                        color: 'var(--p7-ink)',
                      }}
                    >
                      <span style={{ display: 'block', fontWeight: 600 }}>{a.name}</span>
                      {a.archetype ? (
                        <span style={{ display: 'block', marginTop: '1px',
                                       ...MONO,
                                       color: 'var(--p7-ink-subtle)' }}>
                          {String(a.archetype)}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <div style={{ flex: 1, minWidth: 0 }}>
          <ProtectRun
            name={picked ? picked.name : 'Nothing chosen'}
            live={Boolean(picked)}
            ruleCount={ruleCount}
            turns={turns}
            busy={sending}
            composerDisabled={!picked || sending}
            disabledHint={picked ? 'Waiting for the agent…' : 'Choose an agent on the left'}
            onSend={onSend}
            onRecord={picked && onRecord ? () => onRecord(picked) : undefined}
            onChange={picked && onChange ? () => onChange(picked) : undefined}
            emptyHint={picked
              ? 'Nothing has been asked of it yet. Ask it something and the verdict will appear '
                + 'under the answer.'
              : 'Choose an agent on the left to run it.'}
          />
        </div>
      </div>
    </div>
  );
}

const PICKER_NOTE = {
  margin: 0, font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
  lineHeight: 1.5, color: 'var(--p7-ink-subtle)',
};
